'use client';

import { formatCurrency } from '@/lib/utils/currency';

interface GPUHeroSectionProps {
  totalGPUs: number;
  totalRegions: number;
  /** Lowest rentable base rate (the site-wide "from" price). */
  lowestPrice: number;
}

export function GPUHeroSection({ totalGPUs, totalRegions, lowestPrice }: GPUHeroSectionProps) {
  return (
    <section className="relative bg-background overflow-hidden border-b border-border">
      {/* Technical grid background */}
      <div aria-hidden className="absolute inset-0 bg-grid-pattern opacity-60" />
      <div aria-hidden className="absolute inset-0 bg-hero-mesh" />

      {/* Accent line - top */}
      <div aria-hidden className="absolute top-0 left-0 right-0 h-px bg-linear-to-r from-transparent via-primary/40 to-transparent" />

      <div className="relative max-w-6xl mx-auto px-4">
        <div className="py-12 md:py-16">
          <div className="grid grid-cols-12 gap-8 items-end">
            {/* Left column - Typography */}
            <div className="col-span-12 lg:col-span-7">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-8 h-px bg-primary" />
                <span className="text-[11px] font-mono text-primary uppercase tracking-[0.2em]">
                  Cloud GPU machines
                </span>
              </div>

              <h1 className="text-4xl md:text-5xl lg:text-[3.5rem] font-bold text-foreground leading-[1.1] tracking-tight mb-4">
                Rent a GPU machine,
                <br />
                <span className="text-muted-foreground">pay by the minute.</span>
              </h1>

              <p className="text-base text-muted-foreground max-w-md leading-relaxed">
                Full Windows machines with NVIDIA datacenter GPUs for gaming, rendering, AI and
                compute. Start-up takes a few minutes, and usage is metered by the minute at the
                hourly rate.
              </p>
            </div>

            {/* Right column - Stats as technical readout */}
            <div className="col-span-12 lg:col-span-5">
              <div className="lg:border-l lg:border-border lg:pl-8">
                <div className="grid grid-cols-3 gap-4 lg:gap-6">
                  <StatBlock value={totalGPUs} label="GPU sizes" />
                  <StatBlock value={totalRegions} label="Regions" />
                  <StatBlock value={formatCurrency(lowestPrice)} label="From / hour" highlight />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

interface StatBlockProps {
  value: string | number;
  label: string;
  highlight?: boolean;
}

function StatBlock({ value, label, highlight }: StatBlockProps) {
  return (
    <div className="relative">
      <div className="text-2xl md:text-3xl font-bold tabular-nums tracking-tight text-foreground">
        {value}
      </div>
      <div className="text-[10px] font-mono text-muted-foreground uppercase tracking-wider mt-1">
        {label}
      </div>
      {/* Primary is an accent here (a short bar), never the number itself:
          primary-on-background has no contrast guarantee in every palette. */}
      {highlight && <div className="absolute -bottom-2 left-0 w-6 h-0.5 bg-primary" />}
    </div>
  );
}
